import { PropsWithChildren, useEffect } from 'react';
import PageTitle from '@johnlewispartnership/wtr-ingredients/dist/ingredients/PageTitle';
import { getAuthenticationProvider } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { AuthorizationServiceClient } from '@johnlewispartnership/wtr-website-authentication/dist/service';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import config from 'config';
import tokenServiceClient from 'services/token';

import Breadcrumbs from 'renderer/components/Breadcrumbs';
import type { ContentPageData } from 'services/content/types';
import CookieConsentModal from 'renderer/components/CookieConsent/CookieConsentModal';
import { CommonModalProvider } from 'renderer/components/CommonModal/context';
import CommonModal from 'renderer/components/CommonModal';
import { CookieConsentProvider } from 'renderer/components/CookieConsent/context';
import { PreloadedDataProvider } from 'renderer/context/preloadedDataContext';
import { PreloadedData } from 'server/types';

const authorizationServiceClient = new AuthorizationServiceClient(
  config.services.authorizationServer.url,
);

const AuthenticationProvider = getAuthenticationProvider({
  tokenServiceClient,
  tokenServiceBasePath: config.services.tokenClient.path,
  authorizationServiceClient,
});

type PageLayoutProps = PropsWithChildren<{
  pageData: ContentPageData;
  preloadedData?: PreloadedData;
}>;

const PageLayout = ({ children, pageData, preloadedData }: PageLayoutProps) => {
  const { authorableBreadcrumb, breadcrumbs, pageTitle, showPageTitle } = pageData;
  const { display, text } = pageTitle || {};
  const { trackVirtualPageView } = useAnalytics();

  const accessToken = preloadedData?.token?.accessToken;
  const isLoggedIn = !!accessToken;

  useEffect(() => {
    trackVirtualPageView({
      authenticated: isLoggedIn,
      location: window.location.href,
      path: window.location.pathname,
      previous_path: document.referrer,
      service_name: config.applicationName,
      title: document.title,
      type: 'cms',
    });
  }, [isLoggedIn, trackVirtualPageView]);

  return (
    <CookieConsentProvider>
      <AuthenticationProvider
        initialState={{
          initialised: isLoggedIn,
          isLoggedIn: isLoggedIn,
          hasTokenSession: !!preloadedData?.hasSessionToken,
          token: preloadedData?.token,
        }}
      >
        <CommonModalProvider>
          <PreloadedDataProvider preloadedData={preloadedData}>
            <div
              data-testid="container"
              className="container-fluid"
              style={{ width: '100%', padding: 0 }}
            >
              <Breadcrumbs authorableBreadcrumb={authorableBreadcrumb} breadcrumbs={breadcrumbs} />
              {showPageTitle && display && <PageTitle title={text} theme="underline" />}
            </div>
            {children}
          </PreloadedDataProvider>
          <CookieConsentModal />
          <CommonModal />
        </CommonModalProvider>
      </AuthenticationProvider>
    </CookieConsentProvider>
  );
};

export default PageLayout;
